import {Text} from '@/components/atoms'
import {CurrencyType} from '@/types/promotion'
import {PromotionContionsTabList} from '@/utils/constants/promotionConstants'
import {useMemo} from 'react'

const SEPARATOR_OR = 'or'
const SEPARATOR_AND = 'and'

/**
 * usePromotionReview
 * Extracts and memoizes the logic for generating promotion preview strings.
 * @param {object} params
 * @param {Array} params.conditions - Promotion conditions array
 * @param {number} params.activePromoCondition - Active tab index
 * @param {string} params.countryCode - Country code for currency
 * @returns {object} { discountStrings, createPreviewMessage, isMoreBtnVisible }
 */
export function usePromotionReview({conditions, activePromoCondition, countryCode}) {
  const currencyCode = CurrencyType[countryCode ?? 'MX'] ?? '$'
  const getItemNames = (items, separator) => items?.map(i => i.name).join(` ${separator} `) || ''

  const formatFreeProduct = (uom, rewardsQuantity, quantity, rewardItems) => {
    switch (uom) {
      case 'UN':
        return `Get ${rewardsQuantity} Free Product(s) when buying ${quantity} ${rewardItems}`
      case 'CT':
      case 'CS':
        return `Get ${rewardsQuantity} Free Product(s) when buying ${quantity} cases of ${rewardItems}`
      case 'EA':
        return `Get ${rewardsQuantity} Free Product when buying ${currencyCode}${quantity} ${rewardItems}`
      default:
        return null
    }
  }

  const formatDiscountByType = (type, uom, value, quantity, itemNames) => {
    const isCurrency = type === 'AMOUNT'
    const formattedValue = isCurrency ? `${currencyCode}${value}` : `${value}%`
    const quantityPrefix = uom === 'EA' ? `spending ${currencyCode}` : 'buying '
    const quantitySuffix = uom === 'CT' || uom === 'CS' ? 'cases of' : uom === 'EA' ? 'on' : ''

    if (type === 'POINTS') {
      return `Earn ${value} points when ${quantityPrefix}${quantity} ${quantitySuffix} ${itemNames}`
    }
    return `Get ${formattedValue} discount when ${quantityPrefix}${quantity} ${quantitySuffix} ${itemNames}`
  }

  const getDiscountStrings = (conditions, selectedUom) => {
    const isCombinedProduct = conditions?.[0]?.conditionGroup?.conditions?.[0]?.productCombination
      ? SEPARATOR_AND
      : SEPARATOR_OR
    return conditions.flatMap(cond => {
      if (!cond?.rewards?.length) return []
      const condition = cond?.conditionGroup?.conditions?.[0]
      const quantity = selectedUom === 'EA' ? (condition?.amount ?? 0) : (condition?.quantity ?? 0)
      const rewardsQuantity = cond?.rewards?.[0]?.quantity ?? 0
      const itemNames = getItemNames(condition?.items, isCombinedProduct)
      const uom = selectedUom
      return cond.rewards
        .map(reward => {
          if (!reward?.discountType && !reward?.type) return null
          const value = reward?.discountValue ?? reward?.points ?? 0
          if (reward.type === 'FREE_PRODUCT') {
            return formatFreeProduct(uom, rewardsQuantity, quantity, itemNames)
          }
          if (reward.discountType === 'PERCENTAGE' || reward.discountType === 'AMOUNT' || reward.type === 'POINTS') {
            const type = reward.discountType ? reward.discountType : reward.type
            return formatDiscountByType(type, uom, value, quantity, itemNames)
          }
        })
        .filter(Boolean)
    })
  }

  const selectedUom = PromotionContionsTabList[activePromoCondition]?.uom
  const discountStrings = useMemo(
    () => getDiscountStrings(conditions, selectedUom),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conditions, activePromoCondition, selectedUom]
  )
  const isMoreBtnVisible = discountStrings.length > 5

  const createPreviewMessage = (isVisibleAll = false) => {
    const previewList = !isVisibleAll ? discountStrings.slice(0, 5) : discountStrings
    return (
      <div className="flex flex-col">
        {previewList?.map((str, index) => (
          <Text key={index}>
            {str}
            <br />
          </Text>
        ))}
      </div>
    )
  }

  return {discountStrings, createPreviewMessage, isMoreBtnVisible}
}
